import { useEffect, useMemo, useRef } from 'react'
import { useQuery } from '@apollo/react-hooks'
import * as Query from '../../../api/gql/query'
import { buildTree, type Tree } from '../lib/tree'
import type { ScheduleActivity, ScheduleCalendar, ScheduleDetail, ScheduleExternalActivity } from '../types'

const REFETCH_AFTER_MS = 60_000

export const useScheduleDetail = (scheduleId: string | undefined) => {
  const { data, loading, error, refetch } = useQuery(Query.SCHEDULE_DETAIL, { variables: { id: scheduleId }, skip: !scheduleId, notifyOnNetworkStatusChange: false })
  const detail: ScheduleDetail | undefined = data?.schedule
  const lastFetch = useRef(Date.now())
  useEffect(() => { if (data) lastFetch.current = Date.now() }, [data])
  useEffect(() => {
    // review fix (Minor #12): avoid an unhandled rejection on a transient network failure
    const onVisible = () => { if (document.visibilityState === 'visible' && Date.now() - lastFetch.current > REFETCH_AFTER_MS) refetch().catch(() => undefined) }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [refetch])

  const tree: Tree<ScheduleActivity> | null = useMemo(() => (detail ? buildTree(detail.activities) : null), [detail?.activities])
  const byId = useMemo(() => new Map<string, ScheduleActivity>((detail?.activities ?? []).map((a) => [a.id, a])), [detail?.activities])
  const calendarsById = useMemo(() => new Map<string, ScheduleCalendar>((detail?.calendars ?? []).map((c) => [c.id, c])), [detail?.calendars])
  const externalById = useMemo(() => new Map<string, ScheduleExternalActivity>((detail?.externalActivities ?? []).map((e) => [e.id, e])), [detail?.externalActivities])
  return { detail, loading, error, refetch, tree, byId, calendarsById, externalById }
}
